import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getUserProfileApi, updateUserApi, markUsersSeenApi, deleteUserApi, assignXmitApi, changeXmitApi, removeXmitApi } from '../api/users.api';
import { alertMonitoringApi, parseAlertHistoryResult } from '../api/adminAlert.api';
import { apiHandler, unwrapData } from '../utils/apiHandler';
import { useNavBadgeContext } from '../context/NavBadgeContext';
import PageHeader from '../components/PageHeader';
import UserProfileBilling from '../components/UserProfileBilling';
import PlanCell from '../components/users/PlanCell';
import DeleteUserModal from '../components/users/DeleteUserModal';
import AssignXmitModal from '../components/users/AssignXmitModal';
import RemoveXmitModal from '../components/users/RemoveXmitModal';
import ToastBanner from '../components/ToastBanner';
import { formatRelativeDate } from '../utils/userDirectoryFilters';

const ACCOUNT_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'cancelled', label: 'Cancelled' },
];

const VERIFICATION_OPTIONS = [
  { value: 'unverified', label: 'Unverified' },
  { value: 'email_verified', label: 'Email verified' },
  { value: 'mobile_verified', label: 'Mobile verified' },
  { value: 'fully_verified', label: 'Fully verified' },
];

function emptyVehicleFields() {
  return {
    makeModelYear: '',
    licensePlate: '',
    color: '',
    vin: '',
    tireSize: '',
    insuranceCarrier: '',
    fuelType: '',
  };
}

function formatDateInput(value) {
  if (value == null || value === '') return '';
  const s = String(value);
  if (s === 'Invalid Date' || s === 'null' || s === 'undefined') return '';
  return s.includes('T') ? s.slice(0, 10) : s.slice(0, 10);
}

function buildFormFromUser(user) {
  const userInfo = user?.userInfo || {};
  const profileExt = userInfo?.profile || {};
  const vehicle = user?.primaryVehicle || user?.vehicles?.[0] || {};

  return {
    firstName: userInfo.firstName || '',
    lastName: userInfo.lastName || '',
    email: userInfo.email || '',
    mobile: userInfo.mobile || '',
    countryCode: userInfo.countryCode || '+1',
    city: userInfo.city || '',
    state: userInfo.state || '',
    zipCode: userInfo.zipCode || '',
    address: userInfo.address || '',
    timezone: userInfo.timezone || 'America/Chicago',
    gender: userInfo.gender || profileExt.sex || '',
    dateOfBirth: formatDateInput(userInfo.dateOfBirth || profileExt.dateOfBirth),
    status: userInfo.status || 'pending',
    verificationStatus: userInfo.verificationStatus || 'unverified',
    bloodType: profileExt.bloodType || '',
    allergies: profileExt.allergies || '',
    medications: profileExt.medications || '',
    medicalConditions: profileExt.medicalConditions || '',
    heightCm: profileExt.heightCm != null ? String(profileExt.heightCm) : '',
    hairColor: profileExt.hairColor || '',
    primaryLanguage: profileExt.primaryLanguage || 'English',
    responderNotes: profileExt.responderNotes || '',
    emergencyContactName: profileExt.emergencyContactName || '',
    emergencyContactPhone: profileExt.emergencyContactPhone || '',
    emergencyContactCountryCode: profileExt.emergencyContactCountryCode || '+1',
    vehicle: {
      makeModelYear: vehicle.makeModelYear || '',
      licensePlate: vehicle.licensePlate || '',
      color: vehicle.color || '',
      vin: vehicle.vin || '',
      tireSize: vehicle.tireSize || '',
      insuranceCarrier: vehicle.insuranceCarrier || '',
      fuelType: vehicle.fuelType || '',
    },
  };
}

function buildSavePayload(form, { includeVehicle = false } = {}) {
  const payload = {
    firstName: form.firstName.trim() || null,
    lastName: form.lastName.trim() || null,
    email: form.email.trim(),
    mobile: form.mobile.trim() || null,
    countryCode: form.countryCode.trim() || '+1',
    city: form.city.trim() || null,
    state: form.state.trim() || null,
    zipCode: form.zipCode.trim() || null,
    address: form.address.trim() || null,
    timezone: form.timezone.trim() || 'America/Chicago',
    gender: form.gender.trim() || null,
    dateOfBirth: form.dateOfBirth || null,
    status: form.status,
    verificationStatus: form.verificationStatus,
    bloodType: form.bloodType.trim() || null,
    allergies: form.allergies.trim() || null,
    medications: form.medications.trim() || null,
    medicalConditions: form.medicalConditions.trim() || null,
    heightCm: form.heightCm.trim() ? Number(form.heightCm) : null,
    hairColor: form.hairColor.trim() || null,
    primaryLanguage: form.primaryLanguage.trim() || 'English',
    responderNotes: form.responderNotes.trim() || null,
    emergencyContactName: form.emergencyContactName.trim() || null,
    emergencyContactPhone: form.emergencyContactPhone.trim() || null,
    emergencyContactCountryCode: form.emergencyContactCountryCode.trim() || '+1',
  };

  if (includeVehicle && form.vehicle) {
    payload.vehicle = {
      makeModelYear: form.vehicle.makeModelYear.trim() || null,
      licensePlate: form.vehicle.licensePlate.trim() || null,
      color: form.vehicle.color.trim() || null,
      vin: form.vehicle.vin.trim() || null,
      tireSize: form.vehicle.tireSize.trim() || null,
      insuranceCarrier: form.vehicle.insuranceCarrier.trim() || null,
      fuelType: form.vehicle.fuelType.trim() || null,
    };
  }

  return payload;
}

function ReadField({ label, value, mono }) {
  return (
    <div>
      <div className="profile-field-label">{label}</div>
      <div
        className="profile-field-readonly"
        style={mono ? { fontFamily: "'DM Mono',monospace", fontSize: 12 } : undefined}
      >
        {value || '—'}
      </div>
    </div>
  );
}

function EditField({ label, value, onChange, type = 'text', mono, placeholder, className = '' }) {
  return (
    <div className={className}>
      <div className="profile-field-label">{label}</div>
      <input
        type={type}
        className="profile-field-input"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={mono ? { fontFamily: "'DM Mono',monospace", fontSize: 12 } : undefined}
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options, className = '' }) {
  return (
    <div className={className}>
      <div className="profile-field-label">{label}</div>
      <select
        className="profile-field-select"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function TextAreaField({ label, value, onChange, rows = 3, className = '', highlight }) {
  return (
    <div className={className}>
      <div className="profile-field-label">{label}</div>
      <textarea
        className={highlight ? 'staff-notes-textarea' : 'profile-field-input'}
        rows={rows}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        style={highlight ? undefined : { resize: 'vertical', minHeight: 72 }}
      />
    </div>
  );
}

function StatusBadge({ label, tone = 'gray' }) {
  const tones = {
    green: { bg: '#DCFCE7', color: '#15803D' },
    blue: { bg: '#DBEAFE', color: '#1D4ED8' },
    amber: { bg: '#FEF3C7', color: '#B45309' },
    gray: { bg: '#F1F5F9', color: '#475569' },
  };
  const t = tones[tone] || tones.gray;
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        background: t.bg,
        color: t.color,
        padding: '3px 9px',
        borderRadius: 99,
      }}
    >
      {label}
    </span>
  );
}

function verificationTone(status) {
  if (status === 'fully_verified' || status === 'Fully verified') return 'green';
  if (status === 'email_verified' || status === 'Email verified') return 'blue';
  if (status === 'mobile_verified' || status === 'Phone verified') return 'blue';
  return 'amber';
}

function verificationLabel(status) {
  const map = {
    fully_verified: 'Fully verified',
    email_verified: 'Email verified',
    mobile_verified: 'Mobile verified',
    unverified: 'Unverified',
  };
  return map[status] || status || 'Unverified';
}

function inviteStatusTone(status) {
  if (status === 'accepted') return 'green';
  if (status === 'invited' || status === 'pending') return 'amber';
  return 'gray';
}

export default function UserProfile() {
  const navigate = useNavigate();
  const { userId } = useParams();
  const { refreshBadges } = useNavBadgeContext();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [initialFormJson, setInitialFormJson] = useState('');
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showAssignXmitModal, setShowAssignXmitModal] = useState(false);
  const [xmitModalVariant, setXmitModalVariant] = useState('assign');
  const [showRemoveXmitModal, setShowRemoveXmitModal] = useState(false);
  const [assignXmitLoading, setAssignXmitLoading] = useState(false);
  const [removeXmitLoading, setRemoveXmitLoading] = useState(false);
  const [assignXmitError, setAssignXmitError] = useState('');
  const [toast, setToast] = useState(null);
  const [userAlerts, setUserAlerts] = useState([]);
  const [userAlertsLoading, setUserAlertsLoading] = useState(false);

  const userInfo = user?.userInfo || {};
  const enrollment = user?.enrollment || {};
  const flagsAndNotes = user?.flagsAndNotes || {};
  const directory = user?.directory || {};
  const trustedCircle = user?.trustedCircle;
  const quickResponse = user?.quickResponse || {};

  const displayName =
    `${form?.firstName || userInfo?.firstName || ''} ${form?.lastName || userInfo?.lastName || ''}`.trim() ||
    form?.email ||
    userInfo?.email ||
    'Profile';

  const activityRows =
    flagsAndNotes?.recentActivity?.map((row) => ({
      id: row.id || row.event,
      event: row.event,
      date: row.date,
      status: row.status,
      statusColor:
        row.status === 'OK'
          ? '#2E7D32'
          : row.status === 'Failed'
            ? '#B91C1C'
            : '#B45309',
    })) || [];

  async function fetchUserProfile({ silent = false } = {}) {
    if (!silent) setLoading(true);
    const result = await apiHandler(() => getUserProfileApi(userId));
    if (result.success) {
      const data = unwrapData(result);
      setUser(data);
      const nextForm = buildFormFromUser(data);
      setForm(nextForm);
      setInitialFormJson(JSON.stringify(nextForm));
    } else if (!silent) {
      setUser(null);
      setForm(null);
      setInitialFormJson('');
    }
    if (!silent) setLoading(false);
  }

  useEffect(() => {
    fetchUserProfile();
  }, [userId]);

  useEffect(() => {
    async function fetchUserAlerts() {
      if (!userId) return;
      setUserAlertsLoading(true);
      const result = await apiHandler(() =>
        alertMonitoringApi.getHistory({ userId, page: 1, limit: 10 })
      );
      const { rows, error } = parseAlertHistoryResult(result);
      setUserAlerts(error ? [] : rows);
      setUserAlertsLoading(false);
    }
    fetchUserAlerts();
  }, [userId]);

  useEffect(() => {
    async function markSeen() {
      await apiHandler(() => markUsersSeenApi());
      refreshBadges();
    }
    markSeen();
  }, [refreshBadges]);

  const isDirty = form != null && initialFormJson !== '' && JSON.stringify(form) !== initialFormJson;

  function setField(key, value) {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  }

  function setVehicleField(key, value) {
    setForm((prev) => (
      prev ? { ...prev, vehicle: { ...(prev.vehicle || emptyVehicleFields()), [key]: value } } : prev
    ));
  }

  async function saveProfile() {
    if (!form || saving) return { success: false, message: 'Nothing to save' };
    setSaving(true);

    let initialForm = null;
    try {
      initialForm = JSON.parse(initialFormJson);
    } catch {
      initialForm = null;
    }
    const vehicleChanged = initialForm
      ? JSON.stringify(form.vehicle) !== JSON.stringify(initialForm.vehicle)
      : false;

    const payload = buildSavePayload(form, { includeVehicle: vehicleChanged });
    const result = await apiHandler(() => updateUserApi(userId, payload));
    setSaving(false);
    if (result.success) {
      await fetchUserProfile({ silent: true });
      showToast('Profile saved successfully', 'success');
    }
    return result;
  }

  function showToast(msg, type = 'info') {
    setToast({ msg, type });
    const durationMs = type === 'error' ? 5000 : 3200;
    setTimeout(() => setToast(null), durationMs);
  }

  async function handleConfirmDelete(confirmation) {
    setDeleteLoading(true);
    const result = await apiHandler(() => deleteUserApi(userId, confirmation));
    setDeleteLoading(false);

    if (result.success) {
      setShowDeleteModal(false);
      navigate('/users', {
        state: {
          deletedUserToast: `${displayName} deleted successfully`,
          deletedUserToastType: 'success',
        },
      });
      return;
    }

    showToast(
      `Delete failed: ${result.message || 'Could not delete user. Please try again.'}`,
      'error'
    );
  }

  function formatXmitModalError(result, variant = 'assign') {
    if (result.code === 'XMIT_ALREADY_ON_USER') {
      return 'This user already has an XMIT assigned. Use Change XMIT instead.';
    }
    if (result.code === 'XMIT_ALREADY_ASSIGNED') {
      return 'This XMIT number is already assigned to another user.';
    }
    if (result.code === 'SAME_XMIT_NUMBER') {
      return 'Enter a different XMIT number from the current one.';
    }
    if (result.code === 'XMIT_NOT_FOUND') {
      return 'XMIT number was not found in Quick Response.';
    }
    if (result.code === 'QUICK_RESPONSE_DISABLED') {
      return 'Quick Response integration is disabled on the server.';
    }
    if (result.code === 'QR_PROVISION_FAILED' || result.code === 'QR_POOL_ASSIGN_FAILED') {
      return 'Quick Response could not create a new XMIT. Check server logs and QR credentials.';
    }
    return result.message || `Could not ${variant === 'change' ? 'change' : 'assign'} XMIT.`;
  }

  async function handleConfirmAssignXmit({ mode, xmitNumber }) {
    setAssignXmitLoading(true);
    setAssignXmitError('');
    const apiCall = xmitModalVariant === 'change'
      ? () => changeXmitApi(userId, { mode, xmitNumber })
      : () => assignXmitApi(userId, { mode, xmitNumber });
    const result = await apiHandler(apiCall);
    setAssignXmitLoading(false);

    if (result.success) {
      setShowAssignXmitModal(false);
      await fetchUserProfile({ silent: true });
      showToast(
        xmitModalVariant === 'change' ? 'XMIT changed successfully' : 'XMIT assigned successfully',
        'success'
      );
      return;
    }

    setAssignXmitError(formatXmitModalError(result, xmitModalVariant));
  }

  async function handleConfirmRemoveXmit(confirmation) {
    setRemoveXmitLoading(true);
    const result = await apiHandler(() => removeXmitApi(userId, confirmation));
    setRemoveXmitLoading(false);

    if (result.success) {
      setShowRemoveXmitModal(false);
      await fetchUserProfile({ silent: true });
      showToast('XMIT removed successfully', 'success');
      return;
    }

    showToast(result.message || 'Could not remove XMIT.', 'error');
  }

  if (loading) {
    return <p style={{ padding: 24, color: '#64748B' }}>Loading user profile…</p>;
  }

  if (!user || !form) {
    return (
      <div style={{ padding: 24 }}>
        <p style={{ color: '#B91C1C', marginBottom: 12 }}>User not found.</p>
        <button type="button" className="action-btn btn-blue" onClick={() => navigate('/users')}>
          Back to users
        </button>
      </div>
    );
  }

  return (
    <>
      <ToastBanner toast={toast} />

      <PageHeader
        inline
        trail={[
          { label: 'Users', to: '/users' },
          { label: displayName },
        ]}
        actions={
          <>
            <button
              type="button"
              className="action-btn"
              style={{ background: '#F1F5F9', color: '#475569', border: '1px solid #E2E8F0' }}
              onClick={() => navigate(-1)}
            >
              <i className="fas fa-arrow-left" style={{ fontSize: 12 }} />
              Back
            </button>
            <button
              type="button"
              className="action-btn btn-orange"
              disabled={!isDirty || saving}
              onClick={async () => {
                const result = await saveProfile();
                if (!result.success) {
                  showToast(result.message || 'Save failed', 'error');
                }
              }}
            >
              <i className="fas fa-floppy-disk" style={{ fontSize: 12 }} />
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            <button
              type="button"
              className="action-btn"
              style={{ background: '#FEF2F2', color: '#B91C1C', border: '1px solid #FECACA' }}
              onClick={() => setShowDeleteModal(true)}
            >
              <i className="fas fa-trash-can" style={{ fontSize: 12 }} />
              Delete User
            </button>
          </>
        }
      />

      {/* Summary strip */}
      <div
        className="fade-up"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: 16,
          padding: '14px 18px',
          background: '#fff',
          border: '1px solid #E8EDF5',
          borderRadius: 14,
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 12,
            background: 'linear-gradient(135deg,#102A43,#074f78)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 16,
            fontWeight: 700,
            color: '#fff',
            flexShrink: 0,
          }}
        >
          {`${(form?.firstName || userInfo?.firstName)?.[0] || ''}${(form?.lastName || userInfo?.lastName)?.[0] || ''}` || '??'}
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#0F172A' }}>{displayName}</div>
          <div style={{ fontSize: 12, color: '#64748B', marginTop: 2 }}>
            {form?.email || userInfo?.email || '—'}
            {(form?.mobile || userInfo?.mobile) ? ` · ${form?.mobile || userInfo.mobile}` : ''}
          </div>
        </div>
        <StatusBadge
          label={directory.onboarding?.complete ? 'Onboarding complete' : 'Onboarding in progress'}
          tone={directory.onboarding?.complete ? 'green' : 'amber'}
        />
        <StatusBadge
          label={verificationLabel(form.verificationStatus)}
          tone={verificationTone(form.verificationStatus)}
        />
        <StatusBadge label={`Account: ${form.status || 'pending'}`} tone="gray" />
        {isDirty && (
          <span className="profile-save-pill">
            <i className="fas fa-circle" style={{ fontSize: 6 }} />
            Unsaved changes
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-stretch">
        {/* Account & contact */}
        <div className="fade-up delay-1">
          <div className="profile-section-card">
            <div className="profile-section-title">
              <i className="fas fa-id-card" style={{ color: '#ec7426', fontSize: 14, marginRight: 6 }} />
              Account & contact
            </div>
            <p className="profile-edit-hint">
              <i className="fas fa-pen-to-square" style={{ fontSize: 10 }} />
              Editable — use Save changes when done
            </p>
            <div className="profile-form-grid">
              <EditField label="First name" value={form.firstName} onChange={(v) => setField('firstName', v)} />
              <EditField label="Last name" value={form.lastName} onChange={(v) => setField('lastName', v)} />
              <EditField label="Email" value={form.email} onChange={(v) => setField('email', v)} type="email" className="span-2" />
              <EditField label="Country code" value={form.countryCode} onChange={(v) => setField('countryCode', v)} mono />
              <EditField label="Mobile" value={form.mobile} onChange={(v) => setField('mobile', v)} mono />
              <EditField label="Address" value={form.address} onChange={(v) => setField('address', v)} className="span-2" />
              <EditField label="City" value={form.city} onChange={(v) => setField('city', v)} />
              <EditField label="State / region" value={form.state} onChange={(v) => setField('state', v)} />
              <EditField label="Zip code" value={form.zipCode} onChange={(v) => setField('zipCode', v)} />
              <EditField label="Timezone" value={form.timezone} onChange={(v) => setField('timezone', v)} />
              <EditField label="Date of birth" value={form.dateOfBirth} onChange={(v) => setField('dateOfBirth', v)} type="date" />
              <EditField label="Gender" value={form.gender} onChange={(v) => setField('gender', v)} placeholder="e.g. Male, Female" />
              <SelectField
                label="Account status"
                value={form.status}
                onChange={(v) => setField('status', v)}
                options={ACCOUNT_STATUS_OPTIONS}
              />
              <SelectField
                label="Verification"
                value={form.verificationStatus}
                onChange={(v) => setField('verificationStatus', v)}
                options={VERIFICATION_OPTIONS}
              />
            </div>
            <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #F1F5F9', display: 'grid', gap: 12 }}>
              <ReadField label="Auth provider" value={userInfo.authProvider} />
              <ReadField label="Unique ID" value={userInfo.displayId || userInfo.id} mono />
              <ReadField label="Signed up" value={formatRelativeDate(userInfo.createdAt)} />
              <ReadField
                label="Last login"
                value={directory.activity?.label === 'Never' ? 'Never' : `${directory.activity?.label}${directory.activity?.label !== 'Today' ? ' ago' : ''}`}
              />
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <StatusBadge
                  label={userInfo.emailVerified ? 'Email verified' : 'Email unverified'}
                  tone={userInfo.emailVerified ? 'green' : 'amber'}
                />
                <StatusBadge
                  label={userInfo.phoneVerified ? 'Phone verified' : 'Phone unverified'}
                  tone={userInfo.phoneVerified ? 'green' : 'amber'}
                />
              </div>
            </div>
          </div>

          <div className="profile-section-card" style={{ marginTop: 20 }}>
            <div className="profile-section-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <span>
                <i className="fas fa-tower-broadcast" style={{ color: '#074f78', fontSize: 14, marginRight: 6 }} />
                Quick Response / XMIT
              </span>
              {quickResponse.xmitNumber ? (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => {
                      setAssignXmitError('');
                      setXmitModalVariant('change');
                      setShowAssignXmitModal(true);
                    }}
                    className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-sky-200 text-sky-700 bg-sky-50 hover:bg-sky-100"
                  >
                    Change XMIT
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowRemoveXmitModal(true)}
                    className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-amber-200 text-amber-700 bg-amber-50 hover:bg-amber-100"
                  >
                    Remove XMIT
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setAssignXmitError('');
                    setXmitModalVariant('assign');
                    setShowAssignXmitModal(true);
                  }}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-sky-200 text-sky-700 bg-sky-50 hover:bg-sky-100"
                >
                  Assign XMIT
                </button>
              )}
            </div>
            <div style={{ display: 'grid', gap: 14 }}>
              <ReadField label="XMIT number" value={quickResponse.xmitNumber} mono />
              <ReadField label="Site num" value={quickResponse.siteNum} mono />
              <ReadField label="Dev num" value={quickResponse.devNum} mono />
              <ReadField
                label="Verified at"
                value={
                  quickResponse.xmitVerifiedAt
                    ? new Date(quickResponse.xmitVerifiedAt).toLocaleString()
                    : null
                }
              />
              <div>
                <div className="profile-field-label">Alert access</div>
                <div style={{ marginTop: 4 }}>
                  <StatusBadge
                    label={quickResponse.alertAccess ? 'On' : 'Off'}
                    tone={quickResponse.alertAccess ? 'green' : 'gray'}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Onboarding & plan */}
        <div className="fade-up delay-2">
          <div className="profile-section-card" style={{ marginBottom: 20 }}>
            <div className="profile-section-title">
              <i className="fas fa-list-check" style={{ color: '#074f78', fontSize: 14, marginRight: 6 }} />
              Onboarding
            </div>
            <div style={{ display: 'grid', gap: 14 }}>
              <ReadField
                label="Status"
                value={directory.onboarding?.complete ? 'Complete' : 'In progress'}
              />
              {!directory.onboarding?.complete && (
                <>
                  <ReadField label="Current step" value={directory.onboarding?.currentStepLabel} />
                  <ReadField label="Progress" value={directory.onboarding?.progressLabel} />
                </>
              )}
              <ReadField
                label="Completed at"
                value={
                  directory.onboarding?.completedAt
                    ? formatRelativeDate(directory.onboarding.completedAt)
                    : '—'
                }
              />
            </div>
          </div>

          <div className="profile-section-card">
            <div className="profile-section-title">
              <i className="fas fa-credit-card" style={{ color: '#074f78', fontSize: 14, marginRight: 6 }} />
              Plan & billing
            </div>
            <div style={{ marginBottom: 14 }}>
              <PlanCell plan={directory.plan} />
            </div>
            <div style={{ display: 'grid', gap: 14 }}>
              <ReadField label="Billing status" value={enrollment.billingStatus} />
              <ReadField label="Price" value={enrollment.priceLabel} />
              <ReadField label="Enrollment date" value={enrollment.enrollmentDate} />
              <ReadField label="Renewal" value={enrollment.renewalLabel || enrollment.renewalDate} />
              <ReadField label="Trial ends" value={enrollment.trialEndsAt} />
              {enrollment.cancelAtPeriodEnd && (
                <ReadField label="Cancellation" value="Cancels at period end" />
              )}
              <ReadField label="Stripe customer" value={enrollment.stripeCustomerId} mono />
            </div>
          </div>
        </div>
      </div>

      {/* Alert history */}
      <div className="fade-up delay-2" style={{ marginTop: 20 }}>
        <div className="profile-section-card">
          <div
            className="profile-section-title"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
          >
            <span>
              <i className="fas fa-bell" style={{ color: '#C2410C', fontSize: 14, marginRight: 6 }} />
              Alert history
            </span>
            <Link
              to={`/alerts/history?userId=${userId}`}
              className="action-btn btn-blue"
              style={{ padding: '6px 12px', fontSize: 12, textDecoration: 'none' }}
            >
              View all
            </Link>
          </div>
          {userAlertsLoading ? (
            <p style={{ color: '#64748B', fontSize: 13 }}>Loading alerts…</p>
          ) : userAlerts.length === 0 ? (
            <p style={{ color: '#64748B', fontSize: 13 }}>No alerts for this user.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table" style={{ minWidth: 640 }}>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Message</th>
                    <th>Contacts</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {userAlerts.map((alert) => (
                    <tr key={alert.id}>
                      <td style={{ fontSize: 12, color: '#64748B' }}>
                        {new Date(alert.createdAt).toLocaleString()}
                      </td>
                      <td style={{ fontSize: 12, fontWeight: 600 }}>{alert.type?.replace(/_/g, ' ') || '—'}</td>
                      <td>
                        <StatusBadge label={alert.status?.replace(/_/g, ' ') || '—'} tone="gray" />
                      </td>
                      <td style={{ fontSize: 12, color: '#334155', maxWidth: 220 }}>
                        {alert.message
                          ? `${alert.message.slice(0, 60)}${alert.message.length > 60 ? '…' : ''}`
                          : '—'}
                      </td>
                      <td style={{ fontSize: 12, color: '#64748B' }}>
                        {alert.recipientSummary?.total ?? 0}
                      </td>
                      <td>
                        <Link
                          to={`/alerts/${alert.id}`}
                          style={{ fontSize: 12, color: '#074f78', fontWeight: 600, textDecoration: 'none' }}
                        >
                          Details
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Trusted circle */}
      <div className="fade-up delay-2" style={{ marginTop: 20 }}>
        <div className="profile-section-card">
          <div
            className="profile-section-title"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
          >
            <span>
              <i className="fas fa-circle-nodes" style={{ color: '#C2410C', fontSize: 14, marginRight: 6 }} />
              Trusted circle
            </span>
            {trustedCircle?.id && (
              <Link
                to={`/trusted-circles/${trustedCircle.id}`}
                className="action-btn btn-blue"
                style={{ padding: '6px 12px', fontSize: 12, textDecoration: 'none' }}
              >
                Open circle
              </Link>
            )}
          </div>
          {trustedCircle ? (
            <>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                <ReadField label="Circle ID" value={trustedCircle.circleId} mono />
                <ReadField label="Status" value={trustedCircle.status} />
                <ReadField label="State" value={trustedCircle.state} />
                <ReadField
                  label="Contacts"
                  value={directory.trustedContacts?.label || `${trustedCircle.memberCount} contacts`}
                />
              </div>
              {trustedCircle.members?.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <table className="data-table" style={{ minWidth: 700 }}>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Phone</th>
                        <th>Email</th>
                        <th>Relationship</th>
                        <th>Status</th>
                        <th>Added</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trustedCircle.members.map((m) => (
                        <tr key={m.id}>
                          <td style={{ fontWeight: 600, fontSize: 13 }}>
                            {m.name}
                            {m.isEmergencyContact && (
                              <span style={{ fontSize: 10, color: '#C2410C', marginLeft: 6 }}>Emergency</span>
                            )}
                          </td>
                          <td style={{ fontSize: 12, fontFamily: "'DM Mono',monospace" }}>{m.phone || '—'}</td>
                          <td style={{ fontSize: 12, color: '#64748B' }}>{m.email || '—'}</td>
                          <td style={{ fontSize: 12, color: '#64748B' }}>{m.relationship || '—'}</td>
                          <td>
                            <StatusBadge label={m.inviteStatus} tone={inviteStatusTone(m.inviteStatus)} />
                          </td>
                          <td style={{ fontSize: 12, color: '#94A3B8' }}>{formatRelativeDate(m.addedAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p style={{ color: '#64748B', fontSize: 13 }}>No trusted contacts added yet.</p>
              )}
            </>
          ) : (
            <p style={{ color: '#64748B', fontSize: 13 }}>User has not created a trusted circle.</p>
          )}
        </div>
      </div>

      {/* Extended profile / medical */}
      <div className="fade-up delay-3" style={{ marginTop: 20 }}>
        <div className="profile-section-card">
          <div className="profile-section-title">
            <i className="fas fa-heart-pulse" style={{ color: '#BE123C', fontSize: 14, marginRight: 6 }} />
            Profile & medical
          </div>
          <p className="profile-edit-hint">
            <i className="fas fa-notes-medical" style={{ fontSize: 10 }} />
            Medical info shown to responders during alerts
          </p>
          <div className="profile-form-grid">
            <EditField label="Blood type" value={form.bloodType} onChange={(v) => setField('bloodType', v)} placeholder="e.g. O+" />
            <EditField label="Height (cm)" value={form.heightCm} onChange={(v) => setField('heightCm', v)} type="number" />
            <EditField label="Hair color" value={form.hairColor} onChange={(v) => setField('hairColor', v)} />
            <EditField label="Primary language" value={form.primaryLanguage} onChange={(v) => setField('primaryLanguage', v)} />
            <TextAreaField label="Allergies" value={form.allergies} onChange={(v) => setField('allergies', v)} className="span-2" rows={2} />
            <TextAreaField label="Medications" value={form.medications} onChange={(v) => setField('medications', v)} className="span-2" rows={2} />
            <TextAreaField label="Medical conditions" value={form.medicalConditions} onChange={(v) => setField('medicalConditions', v)} className="span-2" rows={2} />
            <EditField label="Emergency contact name" value={form.emergencyContactName} onChange={(v) => setField('emergencyContactName', v)} />
            <EditField label="Emergency country code" value={form.emergencyContactCountryCode} onChange={(v) => setField('emergencyContactCountryCode', v)} mono />
            <EditField label="Emergency phone" value={form.emergencyContactPhone} onChange={(v) => setField('emergencyContactPhone', v)} mono className="span-2" />
            <TextAreaField
              label="Responder notes"
              value={form.responderNotes}
              onChange={(v) => setField('responderNotes', v)}
              className="span-2"
              rows={3}
            />
          </div>
        </div>
      </div>

      {/* Vehicle — from user onboarding (user_vehicles table) */}
      <div className="fade-up delay-3" style={{ marginTop: 20 }}>
        <div className="profile-section-card">
          <div className="profile-section-title">
            <i className="fas fa-car" style={{ color: '#0F766E', fontSize: 14, marginRight: 6 }} />
            Vehicle information
          </div>
          <p className="profile-edit-hint">
            <i className="fas fa-road" style={{ fontSize: 10 }} />
            Saved by user during onboarding — used for roadside assistance
          </p>
          {(user?.vehicles?.length > 1) && (
            <p style={{ fontSize: 12, color: '#64748B', marginBottom: 12 }}>
              Showing primary vehicle ({user.vehicles.length} on file)
            </p>
          )}
          <div className="profile-form-grid">
            <EditField
              label="Make / model / year"
              value={form.vehicle?.makeModelYear}
              onChange={(v) => setVehicleField('makeModelYear', v)}
              placeholder="e.g. Toyota Camry 2020"
              className="span-2"
            />
            <EditField
              label="License plate"
              value={form.vehicle?.licensePlate}
              onChange={(v) => setVehicleField('licensePlate', v)}
              mono
            />
            <EditField
              label="Color"
              value={form.vehicle?.color}
              onChange={(v) => setVehicleField('color', v)}
            />
            <EditField
              label="VIN"
              value={form.vehicle?.vin}
              onChange={(v) => setVehicleField('vin', v)}
              mono
              className="span-2"
            />
            <EditField
              label="Tire size"
              value={form.vehicle?.tireSize}
              onChange={(v) => setVehicleField('tireSize', v)}
            />
            <EditField
              label="Fuel type"
              value={form.vehicle?.fuelType}
              onChange={(v) => setVehicleField('fuelType', v)}
              placeholder="e.g. Gasoline"
            />
            <EditField
              label="Insurance carrier"
              value={form.vehicle?.insuranceCarrier}
              onChange={(v) => setVehicleField('insuranceCarrier', v)}
              className="span-2"
            />
          </div>
          {!form.vehicle?.makeModelYear && !form.vehicle?.licensePlate && !form.vehicle?.vin && (
            <p style={{ fontSize: 13, color: '#94A3B8', marginTop: 14, marginBottom: 0 }}>
              No vehicle on file — user has not completed vehicle onboarding yet.
            </p>
          )}
        </div>
      </div>

      {/* Recent activity */}
      <div className="fade-up delay-3" style={{ marginTop: 20, marginBottom: 20 }}>
        <div className="profile-section-card">
          <div className="profile-section-title">
            <i className="fas fa-clock-rotate-left" style={{ color: '#475569', fontSize: 14, marginRight: 6 }} />
            Recent activity
          </div>
          {activityRows.length > 0 ? (
            <div style={{ border: '1px solid #F1F5F9', borderRadius: 10, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }} className="activity-table">
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Event</th>
                    <th style={{ textAlign: 'left' }}>Date</th>
                    <th style={{ textAlign: 'left' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {activityRows.map((row) => (
                    <tr key={row.id}>
                      <td style={{ fontWeight: 500 }}>{row.event}</td>
                      <td style={{ color: '#64748B' }}>{row.date}</td>
                      <td>
                        <span style={{ color: row.statusColor, fontSize: 11, fontWeight: 600 }}>
                          {row.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ color: '#64748B', fontSize: 13 }}>No recent activity recorded.</p>
          )}
        </div>
      </div>

      <UserProfileBilling userId={userId} />

      <DeleteUserModal
        user={{
          id: userId,
          firstName: userInfo?.firstName,
          lastName: userInfo?.lastName,
          email: userInfo?.email,
        }}
        open={showDeleteModal}
        loading={deleteLoading}
        onCancel={() => {
          if (!deleteLoading) setShowDeleteModal(false);
        }}
        onConfirm={handleConfirmDelete}
      />

      <AssignXmitModal
        user={{
          id: userId,
          firstName: userInfo?.firstName,
          lastName: userInfo?.lastName,
          email: userInfo?.email,
          quickResponse,
        }}
        variant={xmitModalVariant}
        open={showAssignXmitModal}
        loading={assignXmitLoading}
        error={assignXmitError}
        onCancel={() => {
          if (!assignXmitLoading) setShowAssignXmitModal(false);
        }}
        onConfirm={handleConfirmAssignXmit}
      />

      <RemoveXmitModal
        user={{
          id: userId,
          firstName: userInfo?.firstName,
          lastName: userInfo?.lastName,
          email: userInfo?.email,
          quickResponse,
        }}
        open={showRemoveXmitModal}
        loading={removeXmitLoading}
        onCancel={() => {
          if (!removeXmitLoading) setShowRemoveXmitModal(false);
        }}
        onConfirm={handleConfirmRemoveXmit}
      />
    </>
  );
}
